import { useMemo, useState } from 'react';
import { api, User, ScorecardPeriod, WeeklyScorecardRow } from '../lib/api';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import {
  Modal, Table, Th, Tr, Td, Spinner, EmptyState, Badge, IssueTypeBadge, UserName,
} from './ui';

type TypeFilter = 'all' | 'Deliverable' | 'Overhead';

type Tab = 'monthly' | 'weekly';

/**
 * Drill-down for a director/exec viewing their team rollup. Two tabs:
 *  - By month (member breakdown)  — who contributed what for the clicked month.
 *  - By week (Member → Deliverable pivot) — matches the Google Sheet ops
 *    team runs for L10 meetings. Grouped member → engagement → deliverable.
 */
export function TeamDrilldownModal({
  rootUserId,
  rootUserLabel,
  month,
  metricLabel,
  metricValue,
  members,
  kind,
  defaultTab = 'monthly',
  onClose,
}: {
  rootUserId: string;
  rootUserLabel: string;
  month: string;                       // YYYY-MM
  metricLabel: string;
  metricValue: string;
  /** Per-member periods — already in the scorecard response; no re-fetch. */
  members: Array<{ user: User; periods: ScorecardPeriod[]; total_wip_value: number }>;
  /** 'shipped' filters the weekly pivot to Completed deliverables, 'wip' to open. */
  kind: 'shipped' | 'wip';
  defaultTab?: Tab;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  return (
    <Modal
      title={`${rootUserLabel} team — ${metricLabel}`}
      subtitle={<span>{fmtMonth(month)} · <strong>{metricValue}</strong> · {members.length} report{members.length === 1 ? '' : 's'}</span>}
      onClose={onClose}
      width={1200}
    >
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '0.5px solid var(--color-border)' }}>
        <TabButton active={tab === 'monthly'} onClick={() => setTab('monthly')}>
          By month (member breakdown)
        </TabButton>
        <TabButton active={tab === 'weekly'} onClick={() => setTab('weekly')}>
          By week (member × deliverable pivot)
        </TabButton>
        {tab === 'weekly' && <TypeFilterChips value={typeFilter} onChange={setTypeFilter} />}
      </div>
      {tab === 'monthly'
        ? <MonthlyMemberView members={members} month={month} kind={kind} />
        : <WeeklyRollupView rootUserId={rootUserId} kind={kind} typeFilter={typeFilter} />}
    </Modal>
  );
}

function TypeFilterChips({ value, onChange }: { value: TypeFilter; onChange: (v: TypeFilter) => void }) {
  const opts: Array<{ k: TypeFilter; label: string }> = [
    { k: 'all',         label: 'All' },
    { k: 'Deliverable', label: 'Deliverables' },
    { k: 'Overhead',    label: 'Overheads' },
  ];
  return (
    <div style={{ marginLeft: 'auto', marginRight: 14, display: 'flex', gap: 4 }}>
      {opts.map((o) => {
        const active = value === o.k;
        return (
          <button
            key={o.k}
            onClick={() => onChange(o.k)}
            style={{
              fontSize: 10,
              padding: '3px 9px',
              borderRadius: 3,
              border: active ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
              background: active ? 'var(--color-blue-bg)' : 'var(--color-surface)',
              color: active ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none',
        borderBottom: active ? '2px solid var(--color-blue)' : '2px solid transparent',
        padding: '10px 20px', fontSize: 12, fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// ─── Monthly member-breakdown ─────────────────────────────────────────────

function MonthlyMemberView({
  members, month, kind,
}: {
  members: Array<{ user: User; periods: ScorecardPeriod[]; total_wip_value: number }>;
  month: string;
  kind: 'shipped' | 'wip';
}) {
  const rows = useMemo(() => {
    return members
      .map((m) => {
        const p = m.periods.find((pp) => pp.attribution_month === month);
        return {
          user: m.user,
          shipped: p?.shipped_value ?? 0,
          wip: p?.wip_value ?? 0,
          total_wip: m.total_wip_value,
        };
      })
      // Only show members with activity in the metric we're drilling into.
      .filter((r) => kind === 'shipped' ? r.shipped > 0 : (r.wip > 0 || r.total_wip > 0))
      .sort((a, b) => kind === 'shipped' ? b.shipped - a.shipped : b.total_wip - a.total_wip);
  }, [members, month, kind]);

  if (rows.length === 0) {
    return <EmptyState message={`No member contributions in ${fmtMonth(month)}.`} />;
  }

  const totalShipped = rows.reduce((s, r) => s + r.shipped, 0);
  const totalWip     = rows.reduce((s, r) => s + r.wip, 0);

  return (
    <Table>
      <thead>
        <tr>
          <Th>Member</Th>
          <Th>Role</Th>
          <Th>Team</Th>
          <Th right>Shipped ({fmtMonth(month)})</Th>
          <Th right>WIP (due {fmtMonth(month)})</Th>
          <Th right>Cumulative WIP</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Tr key={r.user.jira_user_id}>
            <Td bold><UserName name={r.user.display_name} active={r.user.is_active} /></Td>
            <Td muted style={{ fontSize: 11 }}>{r.user.role}{r.user.title ? ` · ${r.user.title}` : ''}</Td>
            <Td muted style={{ fontSize: 11 }}>{r.user.team}</Td>
            <Td right bold style={{ color: r.shipped > 0 ? 'var(--color-green)' : undefined }}>
              {fmtCurrency(r.shipped)}
            </Td>
            <Td right muted>{fmtCurrency(r.wip)}</Td>
            <Td right muted>{fmtCurrency(r.total_wip)}</Td>
          </Tr>
        ))}
        <tr style={{ borderTop: '0.5px solid var(--color-border-strong)', background: 'var(--color-border)' }}>
          <td colSpan={3} style={{ padding: '9px 16px', fontWeight: 500, fontSize: 12 }}>Team total</td>
          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12, color: 'var(--color-green)' }}>
            {fmtCurrency(totalShipped)}
          </td>
          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12 }}>
            {fmtCurrency(totalWip)}
          </td>
          <td />
        </tr>
      </tbody>
    </Table>
  );
}

// ─── Weekly rollup pivot ──────────────────────────────────────────────────

const WEEKS_OPTIONS = [4, 8, 13, 26];

function WeeklyRollupView({ rootUserId, kind, typeFilter }: {
  rootUserId: string; kind: 'shipped' | 'wip'; typeFilter: TypeFilter;
}) {
  const { config } = useConfig();
  const [weeks, setWeeks] = useState(8);
  const { data, isLoading } = useQuery(
    () => api.scorecard.weekly(rootUserId, { weeks, kind }),
    [rootUserId, weeks, kind],
  );
  const urlFor = (key: string) => jiraIssueUrl(config, key);

  const pivot = useMemo(() => {
    if (!data) return null;
    const filtered = typeFilter === 'all'
      ? data.rows
      : data.rows.filter((r) => r.issue_type === typeFilter);
    return pivotByMember(filtered, data.week_starts);
  }, [data, typeFilter]);

  return (
    <div>
      <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
        <span>Last</span>
        <select
          value={String(weeks)}
          onChange={(e) => setWeeks(parseInt(e.target.value))}
          style={{ fontSize: 11, padding: '2px 6px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)', background: 'var(--color-surface)' }}
        >
          {WEEKS_OPTIONS.map((w) => <option key={w} value={w}>{w} weeks</option>)}
        </select>
        <span>· Mon–Sun buckets · grouped by member → engagement → deliverable</span>
      </div>

      {isLoading ? <Spinner /> : !data || !data.rows.length ? (
        <EmptyState message="No team worklogs in this window." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <RollupPivotTable pivot={pivot!} urlFor={urlFor} />
        </div>
      )}
    </div>
  );
}

interface PivotedDeliv {
  jira_issue_key: string;
  issue_type: 'Deliverable' | 'Overhead';
  deliverable_summary: string;
  deliverable_status: string;
  byWeek: Record<string, { hours: number; value: number }>;
  total: { hours: number; value: number };
}
interface PivotedEng {
  engagement_summary: string | null;
  deliverables: Map<string, PivotedDeliv>;
  byWeek: Record<string, { hours: number; value: number }>;
  total: { hours: number; value: number };
}
interface PivotedMember {
  member_jira_user_id: string;
  member_display_name: string;
  engagements: Map<string, PivotedEng>;
  byWeek: Record<string, { hours: number; value: number }>;
  total: { hours: number; value: number };
}
interface RollupPivot {
  weekStarts: string[];
  members: PivotedMember[];
  byWeek: Record<string, { hours: number; value: number }>;
  grand: { hours: number; value: number };
}

function pivotByMember(rows: WeeklyScorecardRow[], weekStarts: string[]): RollupPivot {
  const members = new Map<string, PivotedMember>();
  const addToBucket = (b: Record<string, { hours: number; value: number }>, w: string, h: number, v: number) => {
    b[w] = { hours: (b[w]?.hours ?? 0) + h, value: (b[w]?.value ?? 0) + v };
  };

  for (const r of rows) {
    const mid = r.member_jira_user_id ?? '__unknown__';
    let mem = members.get(mid);
    if (!mem) {
      mem = {
        member_jira_user_id: mid,
        member_display_name: r.member_display_name ?? '(unknown)',
        engagements: new Map(),
        byWeek: {},
        total: { hours: 0, value: 0 },
      };
      members.set(mid, mem);
    }
    const engKey = r.engagement_jira_issue_id ?? '__none__';
    let eng = mem.engagements.get(engKey);
    if (!eng) {
      eng = {
        engagement_summary: r.engagement_summary,
        deliverables: new Map(),
        byWeek: {},
        total: { hours: 0, value: 0 },
      };
      mem.engagements.set(engKey, eng);
    }
    let deliv = eng.deliverables.get(r.deliverable_jira_issue_id);
    if (!deliv) {
      deliv = {
        jira_issue_key: r.jira_issue_key,
        issue_type: r.issue_type,
        deliverable_summary: r.deliverable_summary,
        deliverable_status: r.deliverable_status,
        byWeek: {},
        total: { hours: 0, value: 0 },
      };
      eng.deliverables.set(r.deliverable_jira_issue_id, deliv);
    }
    addToBucket(deliv.byWeek, r.week_start, r.hours, r.value);
    deliv.total.hours += r.hours;
    deliv.total.value += r.value;
    addToBucket(eng.byWeek, r.week_start, r.hours, r.value);
    eng.total.hours += r.hours;
    eng.total.value += r.value;
    addToBucket(mem.byWeek, r.week_start, r.hours, r.value);
    mem.total.hours += r.hours;
    mem.total.value += r.value;
  }

  const byWeek: Record<string, { hours: number; value: number }> = {};
  const grand = { hours: 0, value: 0 };
  for (const w of weekStarts) byWeek[w] = { hours: 0, value: 0 };
  for (const mem of members.values()) {
    for (const [w, v] of Object.entries(mem.byWeek)) {
      byWeek[w] = { hours: (byWeek[w]?.hours ?? 0) + v.hours, value: (byWeek[w]?.value ?? 0) + v.value };
    }
    grand.hours += mem.total.hours;
    grand.value += mem.total.value;
  }

  return {
    weekStarts,
    members: Array.from(members.values()).sort((a, b) =>
      a.member_display_name.localeCompare(b.member_display_name),
    ),
    byWeek,
    grand,
  };
}

function fmtWeek(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((n) => parseInt(n));
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function RollupPivotTable({
  pivot, urlFor,
}: {
  pivot: RollupPivot;
  urlFor: (key: string) => string | null;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Member / Deliverable</Th>
          {pivot.weekStarts.map((w) => <Th key={w} right>{fmtWeek(w)}</Th>)}
          <Th right>Total</Th>
        </tr>
      </thead>
      <tbody>
        {pivot.members.map((mem) => (
          <MemberGroup key={mem.member_jira_user_id} mem={mem} weekStarts={pivot.weekStarts} urlFor={urlFor} />
        ))}
        <tr style={{ borderTop: '0.5px solid var(--color-border-strong)', background: 'var(--color-border)' }}>
          <td style={{ padding: '9px 16px', fontWeight: 500, fontSize: 12 }}>Grand total</td>
          {pivot.weekStarts.map((w) => (
            <td key={w} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12 }}>
              {pivot.byWeek[w]?.value ? fmtCurrency(pivot.byWeek[w]!.value) : '—'}
            </td>
          ))}
          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 500, fontSize: 12, color: 'var(--color-green)' }}>
            {fmtCurrency(pivot.grand.value)}
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

function MemberGroup({
  mem, weekStarts, urlFor,
}: {
  mem: PivotedMember;
  weekStarts: string[];
  urlFor: (key: string) => string | null;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <>
      <tr
        style={{ background: 'var(--color-blue-bg)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <td style={{ padding: '8px 16px', fontSize: 12, fontWeight: 500 }}>
          <span style={{ display: 'inline-block', width: 12, color: 'var(--color-blue-text)' }}>{collapsed ? '▸' : '▾'}</span>
          {mem.member_display_name}
        </td>
        {weekStarts.map((w) => (
          <td key={w} style={{ padding: '8px 16px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: 'var(--color-blue-text)' }}>
            {mem.byWeek[w]?.value ? fmtCurrency(mem.byWeek[w]!.value) : ''}
          </td>
        ))}
        <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: 'var(--color-blue-text)' }}>
          {fmtCurrency(mem.total.value)}
        </td>
      </tr>
      {!collapsed && Array.from(mem.engagements.values()).map((eng) => (
        <EngagementRows key={eng.engagement_summary ?? '__none__'} eng={eng} weekStarts={weekStarts} urlFor={urlFor} />
      ))}
    </>
  );
}

function EngagementRows({
  eng, weekStarts, urlFor,
}: {
  eng: PivotedEng;
  weekStarts: string[];
  urlFor: (key: string) => string | null;
}) {
  return (
    <>
      <tr style={{ background: 'var(--color-border)' }}>
        <td style={{ padding: '6px 16px 6px 32px', fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)' }}>
          {eng.engagement_summary ?? '(no engagement)'}
        </td>
        {weekStarts.map((w) => (
          <td key={w} style={{ padding: '6px 16px', textAlign: 'right', fontSize: 11, color: 'var(--color-text-muted)' }}>
            {eng.byWeek[w]?.value ? fmtCurrency(eng.byWeek[w]!.value) : ''}
          </td>
        ))}
        <td style={{ padding: '6px 16px', textAlign: 'right', fontSize: 11, color: 'var(--color-text-muted)' }}>
          {fmtCurrency(eng.total.value)}
        </td>
      </tr>
      {Array.from(eng.deliverables.values()).map((d) => {
        const url = urlFor(d.jira_issue_key);
        return (
          <tr key={d.jira_issue_key} style={{ borderBottom: '0.5px solid var(--color-border)' }}>
            <td style={{ padding: '6px 16px 6px 56px', fontSize: 12 }}>
              <IssueTypeBadge issueType={d.issue_type} />
              {url ? (
                <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11, marginRight: 6 }}>
                  {d.jira_issue_key}
                </a>
              ) : (
                <span style={{ color: 'var(--color-text-muted)', fontSize: 11, marginRight: 6 }}>{d.jira_issue_key}</span>
              )}
              {d.deliverable_summary}
              {d.deliverable_status !== 'Completed' && (
                <span style={{ marginLeft: 8 }}><Badge variant="amber">{d.deliverable_status}</Badge></span>
              )}
            </td>
            {weekStarts.map((w) => (
              <td key={w} style={{ padding: '6px 16px', textAlign: 'right', fontSize: 12 }}>
                {d.byWeek[w]?.value ? fmtCurrency(d.byWeek[w]!.value) : ''}
              </td>
            ))}
            <td style={{ padding: '6px 16px', textAlign: 'right', fontSize: 12, fontWeight: 500 }}>
              {fmtCurrency(d.total.value)}
            </td>
          </tr>
        );
      })}
    </>
  );
}
